"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader, CircleCheck, Wrench, ShieldCheck, Clock, MapPin } from "lucide-react";
function MantenimientoForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-500">
                <div className="size-16 bg-secondary/20 rounded-lg flex items-center justify-center mb-4">
                    <CircleCheck className="size-8 text-secondary" />
                </div>
                <h3 className="text-2xl font-default font-bold text-foreground">Application Received</h3>
                <p className="text-muted-foreground max-w-sm">
                    {successMessage}
                </p>
                <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary/5 rounded-md">
                    Submit Another Request
                </Button>
            </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default font-semibold">Full Name</FormLabel>
                            <FormControl>
                                <Input placeholder="John Doe" {...field} className="border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />

                <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
                            <FormLabel className="font-default font-semibold">Email Address</FormLabel>
                            <FormControl>
                                <Input type="email" placeholder="john@example.com" {...field} className="border-0 border-b border-input bg-transparent focus-visible:ring-0 focus-visible:border-primary transition-colors px-0" />
                            </FormControl>
                            <FormMessage />
                        </FormItem>} />
            </div>

            <FormField control={control} name="loan_type" rules={{
      required: "Please select a loan type"
    }} render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold">Loan Type</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                                <SelectTrigger className="border-0 border-b border-input bg-transparent focus:ring-0 focus:border-primary transition-colors px-0">
                                    <SelectValue placeholder="Select your loan interest" />
                                </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                                <SelectItem value="New Home">New Home Purchase</SelectItem>
                                <SelectItem value="Refinance">Refinancing</SelectItem>
                                <SelectItem value="Home Equity">Home Equity Line</SelectItem>
                                <SelectItem value="Commercial">Commercial Mortgage</SelectItem>
                                <SelectItem value="Other">Other Financial Service</SelectItem>
                            </SelectContent>
                        </Select>
                        <FormMessage />
                    </FormItem>} />

            <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
                        <FormLabel className="font-default font-semibold">Additional Notes (Optional)</FormLabel>
                        <FormControl>
                            <textarea className="flex min-h-[100px] w-full border-0 border-b border-input bg-transparent px-0 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-primary transition-colors disabled:cursor-not-allowed disabled:opacity-50 resize-y" placeholder="Tell us about your financial goals..." {...field} />
                        </FormControl>
                        <FormMessage />
                    </FormItem>} />

            {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
                    {submitError}
                </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full h-11 text-base mt-2 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md font-default font-bold tracking-tight">
                {isSubmitting ? <>
                        <Loader className="animate-spin mr-2 size-5" />
                        Processing...
                    </> : 'Start Your Journey'}
            </Button>
        </Form>;
}
export default function StartYourJourney() {
  return <section data-section-id="256420" id="start-your-journey" className="py-16 md:py-24 bg-background text-foreground relative overflow-hidden">
            {}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
                <div className="absolute -top-[10%] -right-[5%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-3xl" />
                <div className="absolute -bottom-[10%] -left-[5%] w-[40%] h-[40%] rounded-lg bg-primary/5 blur-3xl" />
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
                    
                    {}
                    <div className="space-y-8">
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 text-primary text-sm font-mono font-medium">
                                <ShieldCheck className="size-4" />
                                <span>NMLS SECURED LENDING</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl lg:text-6xl font-default font-bold text-foreground leading-tight tracking-tighter">
                                Ready to <span className="text-primary">Move?</span>
                            </h2>
                            <p className="text-xl text-foreground/80 max-w-lg font-default leading-relaxed">
                                Secure your future with Beacon Finance Group. Our expert advisors guide you through every step of the mortgage process with absolute clarity.
                            </p>
                        </div>

                        <div className="space-y-6">
                            {[{
              icon: <Clock className="size-6 text-primary" />,
              title: "Rapid Pre-Approval",
              description: "Get a decision in hours, not days, so you can make your offer with confidence."
            }, {
              icon: <Wrench className="size-6 text-primary" />,
              title: "Custom Loan Structuring",
              description: "Tailored financial solutions designed to fit your unique long-term wealth strategy."
            }, {
              icon: <MapPin className="size-6 text-primary" />,
              title: "Local Market Expertise",
              description: "Deep insights into regional real estate trends to give you a competitive edge."
            }].map((feature, i) => <div key={i} data-index={i} className="flex gap-4">
                                    <div className="shrink-0 mt-1 bg-primary/5 p-2 rounded-md h-fit">
                                        {feature.icon}
                                    </div>
                                    <div>
                                        <h4 className="text-lg font-default font-bold text-foreground">{feature.title}</h4>
                                        <p className="text-sm text-foreground/70 mt-1 font-default">{feature.description}</p>
                                    </div>
                                </div>)}
                        </div>
                    </div>

                    {}
                    <div className="lg:pl-8">
                        <Card className="border-0 shadow-4xl shadow-primary/10 bg-card/50 backdrop-blur-sm rounded-lg">
                            <CardHeader className="pb-4">
                                <CardTitle className="text-2xl font-default font-bold">Get Started Today</CardTitle>
                                <CardDescription className="font-default">
                                    Provide a few details and a Beacon advisor will reach out to discuss your options.
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <FormProvider formKey="start_journey_form_home" formId="start_journey_form_home" sectionName="start-your-journey" defaultValues={{
                name: "",
                email: "",
                loan_type: "",
                message: ""
              }} submitText="Start Your Journey" successMessage="Thank you for choosing Beacon Finance Group. A senior advisor will contact you shortly to begin your personalized consultation.">
                                    <MantenimientoForm />
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>

                </div>
            </div>
        </section>;
}